import { useCallback, useEffect } from "react";
import { updateUserSettingsAction } from "../../../features/Auth/redux/actions";
import { selectUserSettingsLinks } from "../../../features/Auth/redux/selectors";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectVisibleLinks } from "../redux/selectors";
import {
  addVisibleLink,
  removeVisibleLink,
  reorderVisibleLinks,
  setVisibleLinks,
} from "../redux/slice";
import type { ServiceLink, ServiceLinksType } from "../types";

export const useHeaderNav = () => {
  const dispatch = useAppDispatch();
  const userSettingsLinks = useAppSelector(selectUserSettingsLinks);
  const links = useAppSelector(selectVisibleLinks);

  useEffect(() => {
    if (!userSettingsLinks) return;
    dispatch(setVisibleLinks(userSettingsLinks));
  }, [userSettingsLinks]);

  const syncSettings = useCallback(
    (updatedLinks: ServiceLinksType) => {
      dispatch(
        updateUserSettingsAction({
          user_settings: {
            app_links: updatedLinks,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLLIElement>,
    index: number,
  ) => {
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
  };

  const handleChangeVisibleLinks = useCallback(
    (link: ServiceLink) => {
      dispatch(addVisibleLink(link));
      syncSettings([...links, link].slice(0, 6));
    },
    [dispatch, links, syncSettings],
  );

  const handleDeleteLink = useCallback(
    (link: ServiceLink) => {
      dispatch(removeVisibleLink(link));
      syncSettings(links.filter((l) => l !== link));
    },
    [dispatch, links, syncSettings],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLLIElement>, dropIndex: number) => {
      e.preventDefault();
      const dragIndex = Number(e.dataTransfer.getData("text/plain"));
      if (dragIndex === dropIndex) return;

      dispatch(
        reorderVisibleLinks({ fromIndex: dragIndex, toIndex: dropIndex }),
      );

      const newLinks = [...links];
      const [moved] = newLinks.splice(dragIndex, 1);
      newLinks.splice(dropIndex, 0, moved);

      syncSettings(newLinks);
    },
    [dispatch, links, syncSettings],
  );

  return {
    links,
    handleChangeVisibleLinks,
    handleDeleteLink,
    handleDragStart,
    handleDragOver,
    handleDrop,
  };
};
